import { useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Dialog from "@mui/material/Dialog";
import { cloneDeep } from "lodash";
import * as Yup from "yup";
import { ShippingData } from "@/interfaces";
import { ShippingDetails } from "..";
import { ErrorMessage } from "../../atoms";
import { colors } from "@/theme";

export type ShippingDetailsDialogProps = {
  shippingData?: ShippingData;
  open?: boolean;
  onClose?: (shippingData?: ShippingData) => void;
};

const validationSchema = Yup.object().shape({
  shippingContact: Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().required("Email address is required"),
    phone: Yup.string().required("Phone number is required"),
  }),
  shippingAddress: Yup.object().shape({
    street: Yup.string().required("Street is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    zip: Yup.string().required("Zip name is required"),
  }),
});

const ShippingDetailsDialog: React.FC<ShippingDetailsDialogProps> = ({
  shippingData = undefined,
  open = false,
  onClose = undefined,
}) => {
  const [_shippingData, setShippingData] = useState<ShippingData | undefined>(
    cloneDeep(shippingData || {})
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const onConfirmButtonClick = () => {
    let error = undefined;

    setErrorMessage(undefined);

    try {
      validationSchema.validateSync(_shippingData);
    } catch (e) {
      error = true;

      if (e instanceof Yup.ValidationError) {
        if (Array.isArray(e.errors) && e.errors.length) {
          setErrorMessage(e.errors[0]);
        } else {
          setErrorMessage("Unknown validation error");
          console.log(e);
        }
      } else {
        console.log(e);
      }
    }

    if (error) {
      return;
    }

    onClose?.(_shippingData);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={() => {
        onClose?.();
      }}
      sx={{
        "& .MuiDialogContent-root": {
          marginTop: "48px",
        },
      }}
    >
      <IconButton
        aria-label="back"
        onClick={() => {
          onClose?.();
        }}
        sx={{
          position: "absolute",
          left: 8,
          top: 8,
          color: colors.white,
          background: colors.ozone,
          borderRadius: 0,
          padding: "4px",
          "&:hover": {
            color: colors.white,
            background: colors.ozone,
          },
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      <IconButton
        aria-label="close"
        onClick={() => {
          onClose?.();
        }}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: colors.white,
          background: colors.ozone,
          borderRadius: 0,
          padding: "4px",
          "&:hover": {
            color: colors.white,
            background: colors.ozone,
          },
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <ShippingDetails
          shippingData={_shippingData}
          onChange={(shippingData) => setShippingData(shippingData)}
        />
        {!!errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
      </DialogContent>
      <DialogActions
        sx={{
          "&.MuiDialogActions-root": {
            "justify-content": "center",
          },
        }}
      >
        <Button
          onClick={onConfirmButtonClick}
          sx={{
            color: colors.white,
            background: colors.ozone,
            "border-radius": "12px",
            padding: "12px",
            "font-family": "PF DIN Text Pro",
            "font-size": "12px",
            "font-style": "normal",
            "font-weight": "700",
            "line-height": "16px",
            "text-transform": "uppercase",
            "&:hover": {
              color: colors.white,
              background: colors.ozone,
            },
          }}
        >
          CONFIRM SHIPPING INFO
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShippingDetailsDialog;
